import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';
import { BulkDataStructureRequest, BulkDataStructureResponse } from 'src/app/models/bulk_data_structure.model';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { LongTask } from 'src/app/models/long_task.model';
import { LongTaskPusherSerialized } from 'src/app/models/long_task_pusher_serialized.model';
import {
  ProductionPlanification,
  ProductionPlanificationContract,
  ProductionPlanificationOptimalTeamBalanceConfiguration,
} from 'src/app/models/production_planification.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { CompanyProductUserMemberRole } from 'src/app/models/company_product_user_member_role.model';
import { User } from 'src/app/models/user.model';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { WorkingContractVariableCondition } from 'src/app/models/working_contract_variable_condition.model';
import { forkJoin, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ApiService } from 'src/app/services';
import { PusherService } from 'src/app/services/pusher.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  CompanyProductMembersDialogComponent,
  CompanyProductMembersDialogResult,
} from './company-product-members-dialog/company-product-members-dialog.component';
import {
  DatePickerDialogComponent,
  DatePickerDialogData,
} from 'src/app/components/main/shared/date-picker-dialog/date-picker-dialog.component';
import { DayOfWeekEnum } from 'src/app/enums/day-of-week.enum';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateAgoPipe } from 'src/app/pipes/date-ago.pipe';
import { CompanyProductMonthlyEconomicReport } from 'src/app/models/company_product_monthly_economic_report.model';

@Component({
  selector: 'app-production-planification',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    DatePipe,
    DateAgoPipe,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    RouterLink,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './production-planification.component.html',
  styleUrl: './production-planification.component.css',
})
export class ProductionPlanificationComponent implements OnInit, OnDestroy {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private start_date: Date;
  private end_date: Date;
  private fixed_columns: string[] = ['main'];
  private fixed_columns_top: string[] = ['main-top'];
  private weeks: { from: Date; to: Date }[] = [];
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private pusher = inject(PusherService);
  private utils = inject(UtilsService);

  now: Date = new Date();

  selectedYear = signal(dayjs().year());
  availableYears: number[] = this.buildAvailableYears();
  calculated_at = signal<string | null>(null);

  show_sensible_details = signal(false);
  /** true = formato mensual (horas/12), false = formato anual */
  hoursDisplayMonthly = signal(false);
  loading = signal(false);
  progressPercent = signal<number | null>(null);
  longTaskError = signal<string | null>(null);

  dates: ProductionPlanificationDate[] = [];
  dynamic_columns: string[] = [];
  dynamic_columns_top: string[] = [];
  columns: string[] = [];
  columns_top: string[] = [];

  dataSource: MatTableDataSource<ProductionPlanificationRow> = new MatTableDataSource();
  company_products_monthly: { [key: string]: ProductionPlanificationMonthlyForCompanyProduct } = {};
  company_products_hours_role_weekly: {
    [key: string]: {
      [key: string]: { total: number; 'tech-lead': number; 'product-designer': number; 'product-manager': number; developer: number };
    };
  } = {};
  week_role_display: { [weekKey: string]: { [contractKey: string]: WeekRoleDisplayItem[] } } = {};
  /** Resumen anual por contrato: horas previstas (suma meses), previstas_netas (previstas − vacaciones pendientes), horas planificadas (teóricas pactadas). */
  contract_year_summary: {
    [contractId: number]: {
      previstas: number | null;
      previstas_netas: number | null;
      planificadas: number | null;
      vacation_hours_not_taken_obligatory?: number;
    };
  } = {};

  contract_year_summary_tooltips: { [contractId: number]: string } = {};
  optimal_team_balance_configuration: ProductionPlanificationOptimalTeamBalanceConfiguration;

  companyProductUserMemberRoles = toSignal(this.api.getCompanyProductUserMemberRoles().pipe(map(r => r.data)), {
    initialValue: [] as CompanyProductUserMemberRole[],
  });
  companyProductUserMemberRoleSlugToShortName = computed(() => {
    const roles = this.companyProductUserMemberRoles();
    const m: { [key: string]: string } = {};
    roles.forEach(pumr => (m[pumr.slug] = pumr.short_name));
    return m;
  });

  jornadas_laborales: { [key: string]: { [key: string]: number } } = {};
  customPortionModeOverrides = signal<Record<string, 'input' | 'select'>>({});
  portionDisplayModes = signal<('input' | 'select')[][]>([]);
  portionInputValues = signal<number[][]>([]);
  portionSelectValues = signal<(number | null)[][]>([]);
  customPortionValues: Record<string, number> = {};
  expectedProductionPrcntInputValues = signal<(number | null)[][]>([]);
  expectedProductionPrcntDrafts: Record<string, number | null | ''> = {};

  economic_reports_by_key = signal<Record<string, EconomicReportEntry>>({});

  private currentPusherChannel: string | null = null;

  private member_change_in_flight = new Set<string>();

  constructor() {
    this.start_date = dayjs().startOf('year').toDate();
    this.end_date = dayjs().endOf('year').toDate();
  }

  ngOnInit(): void {
    this.fetchProductionPlanification(this.selectedYear());
    this.fetchEconomicReports(this.selectedYear());
  }

  ngOnDestroy(): void {
    // Clean up pusher subscription when component is destroyed
    this.cleanupPusherSubscription();
  }

  onYearChange(): void {
    // Limpiamos datos actuales y recargamos desde backend
    this.dataSource.data = [];
    this.customPortionModeOverrides.set({});
    this.portionDisplayModes.set([]);
    this.portionInputValues.set([]);
    this.portionSelectValues.set([]);
    this.customPortionValues = {};
    this.expectedProductionPrcntInputValues.set([]);
    this.expectedProductionPrcntDrafts = {};
    this.weeks = [];
    this.dates = [];
    this.dynamic_columns = [];
    this.dynamic_columns_top = [];
    this.columns = [];
    this.columns_top = [];
    this.company_products_monthly = {};
    this.company_products_hours_role_weekly = {};
    this.week_role_display = {};
    this.contract_year_summary = {};
    this.jornadas_laborales = {};
    this.economic_reports_by_key.set({});
    this.progressPercent.set(null);

    this.fetchProductionPlanification(this.selectedYear());
    this.fetchEconomicReports(this.selectedYear());
  }

  toggleShowSensibleDetails(): void {
    this.show_sensible_details.update(v => !v);
  }

  toggleHoursDisplayMonthly(): void {
    this.hoursDisplayMonthly.update(v => !v);
  }

  getMonthKey(date: Date): string {
    return dayjs(date).format('YYYY_MM');
  }

  getWeekKey(date: Date): string {
    return dayjs(date).startOf('week').format('YYYY_MM_DD') + '_' + dayjs(date).endOf('week').format('YYYY_MM_DD');
  }

  customPortionKey(row_idx: number, date_idx: number): string {
    return `${row_idx}_${date_idx}`;
  }

  onExpectedProductionPrcntInputValueChange(row_idx: number, date_idx: number, value: number | null | ''): void {
    const key = this.expectedProductionPrcntKey(row_idx, date_idx);
    this.expectedProductionPrcntDrafts[key] = value;
    this.expectedProductionPrcntInputValues.update(curr => {
      const next = curr.map(row => (row != null ? [...row] : []));
      if (next[row_idx] == null) {
        next[row_idx] = [];
      }
      next[row_idx][date_idx] = value === '' || value == null ? null : Number(value);
      return next;
    });
  }

  applyExpectedProductionPrcnt(row_idx: number, date_idx: number): void {
    const week_key =
      dayjs(this.dates[date_idx].date).startOf('week').format('YYYY_MM_DD') +
      '_' +
      dayjs(this.dates[date_idx].date).endOf('week').format('YYYY_MM_DD');
    const current = this.dataSource.data[row_idx].weeks[week_key]?.company_product_user_member?.expected_production_prcnt ?? null;
    const key = this.expectedProductionPrcntKey(row_idx, date_idx);
    const draft = this.expectedProductionPrcntDrafts[key];

    let value: number | null;
    if (draft === undefined) {
      value = current;
    } else if (draft === '' || draft == null || Number.isNaN(Number(draft))) {
      value = null;
    } else {
      value = Math.max(0, Math.min(100, Math.round(Number(draft))));
    }

    if (value === current) {
      delete this.expectedProductionPrcntDrafts[key];
      this.expectedProductionPrcntInputValues.update(curr => {
        const next = curr.map(row => (row != null ? [...row] : []));
        if (next[row_idx] == null) {
          next[row_idx] = [];
        }
        next[row_idx][date_idx] = value;
        return next;
      });
      return;
    }

    this.onMemberFieldChange(row_idx, date_idx, 'expected_production_prcnt', value);
  }

  toggleCustomPortionMode(row_idx: number, date_idx: number, currentValue: number): void {
    const key = this.customPortionKey(row_idx, date_idx);
    const currentMode = this.portionDisplayModes()[row_idx]?.[date_idx] ?? this.resolvePortionDisplayMode(row_idx, date_idx);
    const nextMode: 'input' | 'select' = currentMode === 'input' ? 'select' : 'input';
    this.customPortionModeOverrides.update(curr => ({
      ...curr,
      [key]: nextMode,
    }));
    const inputValue = Number(currentValue ?? 0);
    if (nextMode === 'input') {
      this.customPortionValues[key] = inputValue;
      this.patchPortionCellMatrices(row_idx, date_idx, { mode: nextMode, input: inputValue });
    } else {
      this.patchPortionCellMatrices(row_idx, date_idx, { mode: nextMode });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const selectHost = document.querySelector(`[data-portion-key="${key}"]`) as HTMLElement | null;
          selectHost?.click();
        });
      });
    }
  }

  onCustomPortionInputValueChange(row_idx: number, date_idx: number, value: number): void {
    const key = this.customPortionKey(row_idx, date_idx);
    this.customPortionValues[key] = value;
    this.patchPortionCellMatrices(row_idx, date_idx, { input: value });
  }

  applyCustomPortion(row_idx: number, date_idx: number): void {
    const key = this.customPortionKey(row_idx, date_idx);
    const raw = Number(this.customPortionValues[key]);
    if (Number.isNaN(raw)) {
      this.customPortionModeOverrides.update(curr => {
        const next = { ...curr };
        delete next[key];
        return next;
      });
      const resolvedMode = this.resolvePortionDisplayMode(row_idx, date_idx);
      const current = this.getCurrentPortionValue(row_idx, date_idx);
      this.patchPortionCellMatrices(row_idx, date_idx, {
        mode: resolvedMode,
        input: current,
        select: this.isPresetPortionValue(current) ? current : null,
      });
      return;
    }
    const value = Math.max(0, Math.min(1, raw));
    const current = this.getCurrentPortionValue(row_idx, date_idx);
    if (Math.abs(value - current) < 0.000001) {
      this.customPortionValues[key] = current;
      this.customPortionModeOverrides.update(curr => {
        const next = { ...curr };
        delete next[key];
        return next;
      });
      const mode = this.isPresetPortionValue(current) ? 'select' : 'input';
      this.patchPortionCellMatrices(row_idx, date_idx, {
        mode,
        input: current,
        select: mode === 'select' ? current : null,
      });
      return;
    }

    // Keep draft until the scope dialog is confirmed; do not mutate committed cell state yet.
    this.customPortionValues[key] = value;
    this.onMemberFieldChange(row_idx, date_idx, 'portion_of_productive_work_day', value);
  }

  getRoleProgressCurrPrcnt(weekKey: string, contractId: number, roleKey: string, roleValue: number) {
    const contractData = this.company_products_hours_role_weekly[weekKey]?.[`contract_${contractId}`];
    if (!contractData || !this.optimal_team_balance_configuration) {
      return 0;
    }

    const totalHours = contractData.total;
    if (totalHours === 0) {
      return 0;
    }

    return (roleValue / totalHours) * 100;
  }

  getRoleProgressOptimalPrcnt(weekKey: string, contractId: number, roleKey: string) {
    const contractData = this.company_products_hours_role_weekly[weekKey]?.[`contract_${contractId}`];
    if (!contractData || !this.optimal_team_balance_configuration) {
      return 0;
    }

    const totalHours = contractData.total;
    if (totalHours === 0) {
      return 0;
    }

    const hasProductDesigner = contractData['product-designer'] > 0;
    const optimalConfig = hasProductDesigner
      ? this.optimal_team_balance_configuration.with_product_design_prcnt
      : this.optimal_team_balance_configuration.without_product_design_prcnt;

    const backendRoleKey = this.normalizeRoleKeyForOptimal(roleKey);
    const optimalPercentage = backendRoleKey ? ((optimalConfig as Record<string, number>)[backendRoleKey] ?? 0) : 0;
    if (optimalPercentage === 0) {
      return 0;
    }

    return optimalPercentage;
  }

  getRoleProgressPercentage(weekKey: string, contractId: number, roleKey: string, roleValue: number): number {
    const optimalPercentage = this.getRoleProgressOptimalPrcnt(weekKey, contractId, roleKey);
    const currPrcnt = this.getRoleProgressCurrPrcnt(weekKey, contractId, roleKey, roleValue);

    if (currPrcnt === 0 || optimalPercentage === 0) return 0;
    return (currPrcnt / optimalPercentage) * 100;
  }

  getProgressBarColor(weekKey: string, contractId: number, roleKey: string, roleValue: number): string {
    const optimalPercentage = this.getRoleProgressOptimalPrcnt(weekKey, contractId, roleKey);
    const currPrcnt = this.getRoleProgressCurrPrcnt(weekKey, contractId, roleKey, roleValue);

    const diff = optimalPercentage - currPrcnt;

    if (diff > 5 || diff < -5) {
      return 'red'; // Rojo para diferencias grandes
    } else if (diff > 2 || diff < -2) {
      return 'yellow'; // Amarillo para diferencias medianas
    } else {
      return 'accent';
    }
  }

  onForceRecalculate() {
    this.loading.set(true);
    this.progressPercent.set(null);
    this.longTaskError.set(null);
    this.dataSource.data = [];
    this.customPortionModeOverrides.set({});
    this.portionDisplayModes.set([]);
    this.portionInputValues.set([]);
    this.portionSelectValues.set([]);
    this.customPortionValues = {};
    this.expectedProductionPrcntInputValues.set([]);
    this.expectedProductionPrcntDrafts = {};
    this.cleanupPusherSubscription();

    this.api.postProductionPlanificationCalculate(this.selectedYear()).subscribe({
      next: longTask => {
        if (longTask.error_message) {
          console.error('Error forcing production planification:', longTask.error_message);
          this.loading.set(false);
          return;
        }
        this.currentPusherChannel = longTask.pusher_channel_to_check;
        this.pusher.subscribeToLongTaskChannel(this.currentPusherChannel, (data: LongTaskPusherSerialized) =>
          this.handleLongTaskUpdate(data)
        );
        this.pusher.subscribeToLongTaskProgress(this.currentPusherChannel, (data: { progress?: number; message?: string }) => {
          this.progressPercent.set(data.progress ?? null);
        });
      },
      error: err => {
        console.error('Error forcing production planification:', err);
        this.loading.set(false);
      },
    });
  }

  openMembersDialog(row_idx: number) {
    this.dialog
      .open(CompanyProductMembersDialogComponent, {
        width: '1280px',
        maxWidth: '95vw',
        data: {
          company_product_id: this.dataSource.data[row_idx].company_product_id,
          company_product_title: this.dataSource.data[row_idx].company_product_title,
        },
      })
      .afterClosed()
      .subscribe((result: CompanyProductMembersDialogResult | undefined) => {
        if (result?.saved) {
          this.applyMembersDialogResult(result);
        }
      });
  }

  openEconomicReportDetail(report_id: number): void {
    this.router.navigate(['/employees', 'production', 'company-product-monthly-economic-reports', report_id]);
  }

  deleteUserFromCompanyProduct(row_idx: number, date_idx: number) {
    const gate_key = this.memberChangeGateKey(row_idx, date_idx);
    if (this.member_change_in_flight.has(gate_key)) {
      return;
    }
    this.member_change_in_flight.add(gate_key);

    if (!confirm('¿Estás seguro que quieres eliminar del producto a partir de esta semana?')) {
      this.member_change_in_flight.delete(gate_key);
      return;
    }

    const monday = dayjs(this.dates[date_idx].date).startOf('week');
    const week_key = this.getWeekKey(monday.toDate());
    const current_pum = this.dataSource.data[row_idx].weeks[week_key]?.company_product_user_member;
    if (current_pum == null) {
      this.member_change_in_flight.delete(gate_key);
      return;
    }

    const body: BulkDataStructureRequest<CompanyProductUserMember> = { create_or_update: [], delete: [] };
    const last_sunday = monday.subtract(1, 'day');
    const starts_this_week = dayjs(current_pum.start_date).isSame(monday, 'day');

    // Membership started earlier and still covers this week: close it at the previous Sunday.
    // Do not touch a distinct previous-week CPUM — exclusion constraints already force it to end before this one.
    if (!starts_this_week) {
      body.create_or_update.push({
        id: current_pum.id,
        end_date: this.utils.normalizeMadridDateForApi(last_sunday.toDate(), 'end'),
      } as unknown as CompanyProductUserMember);
    }

    const ids_to_delete = new Set<number>();
    for (let date_idx_it = date_idx; date_idx_it < this.dynamic_columns.length; date_idx_it++) {
      const week_key_it = this.getWeekKey(this.dates[date_idx_it].date);
      const pum = this.dataSource.data[row_idx].weeks[week_key_it]?.company_product_user_member;
      if (pum == null) {
        continue;
      }
      if (starts_this_week || pum.id !== current_pum.id) {
        ids_to_delete.add(pum.id);
      }
    }
    body.delete = [...ids_to_delete];

    this.api
      .bulkMembers(this.dataSource.data[row_idx].company_product_id, body)
      .pipe(finalize(() => this.member_change_in_flight.delete(gate_key)))
      .subscribe(() => {
        let date_idx_it = date_idx;
        const date_idx_max = this.dynamic_columns.length;
        while (date_idx_it < date_idx_max) {
          const week_key_it = this.getWeekKey(this.dates[date_idx_it].date);
          delete this.dataSource.data[row_idx].weeks[week_key_it];
          date_idx_it++;
        }

        this.refreshPlanificationUiAfterMemberMutation(this.dataSource.data[row_idx].company_product_id);
      });
  }

  onMemberFieldChange(
    row_idx: number,
    date_idx: number,
    field: 'company_product_user_member_role_id' | 'portion_of_productive_work_day' | 'expected_production_prcnt',
    value: number | null
  ) {
    const gate_key = this.memberChangeGateKey(row_idx, date_idx);
    if (this.member_change_in_flight.has(gate_key)) {
      return;
    }

    const monday = dayjs(this.dates[date_idx].date).startOf('week');
    const week_key = this.getWeekKey(monday.toDate());
    const current_pum = this.dataSource.data[row_idx].weeks[week_key]?.company_product_user_member;
    if (current_pum == null) {
      return;
    }

    const current_value = current_pum[field] as number | null;
    if (field === 'portion_of_productive_work_day') {
      if (current_value != null && value != null && Math.abs(Number(current_value) - Number(value)) < 0.000001) {
        this.revertMemberFieldDrafts(row_idx, date_idx);
        return;
      }
    } else if (current_value === value) {
      this.revertMemberFieldDrafts(row_idx, date_idx);
      return;
    }

    this.member_change_in_flight.add(gate_key);
    const min_sunday = monday.endOf('week').toDate();
    const dialog_data: DatePickerDialogData = {
      dialog_title: '¿Hasta cuándo aplicar el cambio?',
      dialog_ok_button: 'Confirmar',
      label: 'Fecha de fin (domingo)',
      selected_date: min_sunday,
      min_date: min_sunday,
      filter: DayOfWeekEnum.Sunday,
    };

    queueMicrotask(() => {
      this.dialog
        .open(DatePickerDialogComponent, {
          width: '400px',
          data: dialog_data,
        })
        .afterClosed()
        .subscribe((end_date: Date | undefined) => {
          if (end_date == null) {
            this.revertMemberFieldDrafts(row_idx, date_idx);
            this.member_change_in_flight.delete(gate_key);
            return;
          }

          this.commitMemberFieldChange(row_idx, date_idx, field, value, end_date, gate_key);
        });
    });
  }

  private memberChangeGateKey(row_idx: number, date_idx: number): string {
    return `${row_idx}_${date_idx}`;
  }

  private revertMemberFieldDrafts(row_idx: number, date_idx: number): void {
    const portion_key = this.customPortionKey(row_idx, date_idx);
    delete this.customPortionValues[portion_key];
    this.customPortionModeOverrides.update(curr => {
      const next = { ...curr };
      delete next[portion_key];
      return next;
    });

    const expected_key = this.expectedProductionPrcntKey(row_idx, date_idx);
    delete this.expectedProductionPrcntDrafts[expected_key];
    this.rebuildPortionCellState();
  }

  private commitMemberFieldChange(
    row_idx: number,
    date_idx: number,
    field: 'company_product_user_member_role_id' | 'portion_of_productive_work_day' | 'expected_production_prcnt',
    value: number | null,
    end_date: Date,
    gate_key: string
  ): void {
    const monday = dayjs(this.dates[date_idx].date).startOf('week');
    const week_key = this.getWeekKey(monday.toDate());
    const last_monday = date_idx > 0 ? dayjs(this.dates[date_idx - 1].date).startOf('week') : null;
    const last_week_key = last_monday != null ? this.getWeekKey(last_monday.toDate()) : null;
    const current_pum = this.dataSource.data[row_idx].weeks[week_key].company_product_user_member;
    const previous_pum =
      last_week_key != null ? this.dataSource.data[row_idx].weeks[last_week_key]?.company_product_user_member : undefined;

    const body = this.buildMemberFieldChangeBulkBody(row_idx, monday, current_pum, previous_pum, field, value, end_date);
    const touched_member_ids = new Set<number>([current_pum.id]);
    if (previous_pum != null) {
      touched_member_ids.add(previous_pum.id);
    }

    this.api
      .bulkMembers(this.dataSource.data[row_idx].company_product_id, body)
      .pipe(finalize(() => this.member_change_in_flight.delete(gate_key)))
      .subscribe(data => {
        this.paintMembersOntoRowFromBulk(row_idx, date_idx, data, touched_member_ids);
        this.refreshPlanificationUiAfterMemberMutation(this.dataSource.data[row_idx].company_product_id, {
          clear_field_drafts: true,
        });
      });
  }

  private buildMemberFieldChangeBulkBody(
    row_idx: number,
    monday: dayjs.Dayjs,
    current_pum: CompanyProductUserMember,
    previous_pum: CompanyProductUserMember | undefined,
    field: 'company_product_user_member_role_id' | 'portion_of_productive_work_day' | 'expected_production_prcnt',
    value: number | null,
    end_date: Date
  ): BulkDataStructureRequest<CompanyProductUserMember> {
    const body: BulkDataStructureRequest<CompanyProductUserMember> = { create_or_update: [], delete: [] };
    const scope_end = dayjs(end_date).endOf('day');
    const original_end = current_pum.end_date != null ? dayjs(current_pum.end_date) : null;
    const needs_continuation = original_end == null || original_end.isAfter(scope_end, 'day');
    const continuation_start = scope_end.add(1, 'day').startOf('day');
    const next_values = {
      company_product_user_member_role_id:
        field === 'company_product_user_member_role_id' ? Number(value) : current_pum.company_product_user_member_role_id,
      portion_of_productive_work_day:
        field === 'portion_of_productive_work_day' ? Number(value) : current_pum.portion_of_productive_work_day,
      expected_production_prcnt: field === 'expected_production_prcnt' ? value : current_pum.expected_production_prcnt,
    };
    const previous_values = {
      company_product_user_member_role_id: current_pum.company_product_user_member_role_id,
      portion_of_productive_work_day: current_pum.portion_of_productive_work_day,
      expected_production_prcnt: current_pum.expected_production_prcnt,
    };

    if (previous_pum != null && this.wouldMemberMatchPreviousWeekAfterChange(current_pum, previous_pum, field, value)) {
      body.delete.push(previous_pum.id, current_pum.id);
      body.create_or_update.push(
        this.buildNewMemberPayload(row_idx, dayjs(previous_pum.start_date).toDate(), scope_end.toDate(), next_values)
      );
    } else if (dayjs(current_pum.start_date).isSame(monday, 'day')) {
      body.delete.push(current_pum.id);
      body.create_or_update.push(this.buildNewMemberPayload(row_idx, monday.toDate(), scope_end.toDate(), next_values));
    } else {
      body.create_or_update.push({
        id: current_pum.id,
        end_date: this.utils.normalizeMadridDateForApi(monday.subtract(1, 'day').toDate(), 'end'),
      } as unknown as CompanyProductUserMember);
      body.create_or_update.push(this.buildNewMemberPayload(row_idx, monday.toDate(), scope_end.toDate(), next_values));
    }

    if (needs_continuation) {
      body.create_or_update.push(
        this.buildNewMemberPayload(row_idx, continuation_start.toDate(), original_end?.toDate() ?? null, previous_values)
      );
    }

    return body;
  }

  private refreshPlanificationUiAfterMemberMutation(company_product_id: number, options: { clear_field_drafts?: boolean } = {}): void {
    this.dataSource.data = this.processValidationFlags(this.dataSource.data);
    if (options.clear_field_drafts) {
      this.customPortionValues = {};
      this.customPortionModeOverrides.set({});
      this.expectedProductionPrcntDrafts = {};
    }
    this.rebuildPortionCellState();
    this.rebuildExpectedProductionPrcntCellState();
    this.markContractYearSummaryUnknownForProduct(company_product_id);
    this.rebuildCompanyProductHoursRoleWeekly();
    this.buildWeekRoleDisplay();
    this.buildContractYearSummary();
  }

  private buildNewMemberPayload(
    row_idx: number,
    start_date: Date,
    end_date: Date | null,
    values: {
      company_product_user_member_role_id: number;
      portion_of_productive_work_day: number;
      expected_production_prcnt: number | null;
    }
  ): CompanyProductUserMember {
    return {
      user_id: this.dataSource.data[row_idx].user_id,
      company_product_id: this.dataSource.data[row_idx].company_product_id,
      start_date: this.utils.normalizeMadridDateForApi(start_date, 'start') as unknown as Date,
      end_date: end_date != null ? (this.utils.normalizeMadridDateForApi(end_date, 'end') as unknown as Date) : null,
      company_product_user_member_role_id: values.company_product_user_member_role_id,
      portion_of_productive_work_day: values.portion_of_productive_work_day,
      expected_production_prcnt: values.expected_production_prcnt,
    } as CompanyProductUserMember;
  }

  private paintMembersOntoRowFromBulk(
    row_idx: number,
    from_date_idx: number,
    data: BulkDataStructureResponse<CompanyProductUserMember>,
    touched_member_ids: Set<number>
  ): void {
    const members = [...(data.created ?? []), ...(data.updated ?? [])].map(m => this.withResolvedMemberRole(m));
    for (const member of members) {
      touched_member_ids.add(member.id);
    }
    for (const deleted of data.deleted ?? []) {
      touched_member_ids.add(deleted.id);
    }

    this.paintMembersOntoRowWeeks(row_idx, from_date_idx, members, touched_member_ids, null);
  }

  private applyMembersDialogResult(result: CompanyProductMembersDialogResult): void {
    const members = this.resolveMembersFromDialogResult(result);
    const deleted_ids = new Set((result.bulk.deleted ?? []).map(d => d.id));
    const touched_member_ids = new Set<number>([...deleted_ids, ...members.map(m => m.id)]);
    const affected_user_ids = this.collectAffectedUserIdsFromDialogResult(result, members, deleted_ids);

    this.fetchMissingWorkingContractsForUsers([...affected_user_ids]).subscribe(working_contracts_by_user => {
      affected_user_ids.forEach(user_id => {
        const user_members = members.filter(m => m.user_id === user_id);
        const row_idx = this.ensurePlanificationRowForUser(result, user_id, user_members);
        if (row_idx < 0) {
          return;
        }

        const wc_template = this.findWorkingContractTemplateForUser(user_id) ?? working_contracts_by_user.get(user_id) ?? null;
        this.paintMembersOntoRowWeeks(row_idx, 0, user_members, touched_member_ids, wc_template);
      });

      this.refreshPlanificationUiAfterMemberMutation(result.company_product_id);
    });
  }

  private resolveMembersFromDialogResult(result: CompanyProductMembersDialogResult): CompanyProductUserMember[] {
    return [...(result.bulk.created ?? []), ...(result.bulk.updated ?? [])].map(m => {
      const with_role = this.withResolvedMemberRole(m);
      const user = with_role.user ?? result.users_by_id[with_role.user_id];
      return user != null ? { ...with_role, user } : with_role;
    });
  }

  private collectAffectedUserIdsFromDialogResult(
    result: CompanyProductMembersDialogResult,
    members: CompanyProductUserMember[],
    deleted_ids: Set<number>
  ): Set<number> {
    const affected_user_ids = new Set<number>(members.map(m => m.user_id));
    this.dataSource.data.forEach(row => {
      if (row.company_product_id !== result.company_product_id) {
        return;
      }
      Object.values(row.weeks).forEach(week => {
        const pum_id = week?.company_product_user_member?.id;
        if (pum_id != null && deleted_ids.has(pum_id)) {
          affected_user_ids.add(row.user_id);
        }
      });
    });
    return affected_user_ids;
  }

  private fetchMissingWorkingContractsForUsers(user_ids: number[]) {
    const users_needing_working_contract = user_ids.filter(user_id => this.findWorkingContractTemplateForUser(user_id) == null);
    if (users_needing_working_contract.length === 0) {
      return of(new Map<number, WorkingContract>());
    }

    return forkJoin(
      users_needing_working_contract.reduce(
        (acc, user_id) => {
          acc[user_id] = this.api.getActiveWorkingContract(user_id, {
            with: 'working_contract_variable_conditions.department_categories.department_category_variable_conditions',
          });
          return acc;
        },
        {} as Record<number, ReturnType<ApiService['getActiveWorkingContract']>>
      )
    ).pipe(
      map(fetched_wcs => {
        const working_contracts_by_user = new Map<number, WorkingContract>();
        Object.entries(fetched_wcs).forEach(([user_id, wc]) => {
          working_contracts_by_user.set(Number(user_id), wc);
        });
        return working_contracts_by_user;
      })
    );
  }

  private ensurePlanificationRowForUser(
    result: CompanyProductMembersDialogResult,
    user_id: number,
    user_members: CompanyProductUserMember[]
  ): number {
    const existing_idx = this.dataSource.data.findIndex(
      row => row.company_product_id === result.company_product_id && row.user_id === user_id
    );
    if (existing_idx >= 0) {
      return existing_idx;
    }

    let sibling_idx = -1;
    for (let i = this.dataSource.data.length - 1; i >= 0; i--) {
      if (this.dataSource.data[i].company_product_id === result.company_product_id) {
        sibling_idx = i;
        break;
      }
    }
    const sibling = sibling_idx >= 0 ? this.dataSource.data[sibling_idx] : undefined;
    const user = result.users_by_id[user_id] ?? user_members[0]?.user;
    if (sibling == null || user == null) {
      return -1;
    }

    const new_row: ProductionPlanificationRow = {
      contract_id: sibling.contract_id,
      is_first_row_of_contract: false,
      is_last_row_of_contract: false,
      company_product_id: result.company_product_id,
      company_product_title: sibling.company_product_title,
      user_id,
      user_name: user.name,
      user_surnames: user.surnames,
      weeks: {},
    };
    const next_data = [...this.dataSource.data];
    next_data.splice(sibling_idx + 1, 0, new_row);
    this.dataSource.data = next_data;
    return sibling_idx + 1;
  }

  private paintMembersOntoRowWeeks(
    row_idx: number,
    from_date_idx: number,
    members: CompanyProductUserMember[],
    touched_member_ids: Set<number>,
    working_contract_template: WorkingContract | null
  ): void {
    const date_idx_max = this.dynamic_columns.length;
    const row = this.dataSource.data[row_idx];

    for (let date_idx_it = from_date_idx; date_idx_it < date_idx_max; date_idx_it++) {
      if (!this.dates[date_idx_it].is_first_day_of_week) {
        continue;
      }

      const week_key_it = this.getWeekKey(this.dates[date_idx_it].date);
      const week_data = row.weeks[week_key_it];
      const week_start = dayjs(this.dates[date_idx_it].date).startOf('week');
      const week_end = dayjs(this.dates[date_idx_it].date).endOf('week');
      const covering = members.find(m => {
        const start = dayjs(m.start_date);
        const end = m.end_date != null ? dayjs(m.end_date) : null;
        return start.isSameOrBefore(week_end, 'day') && (end == null || end.isSameOrAfter(week_start, 'day'));
      });

      if (covering != null) {
        if (week_data != null) {
          week_data.company_product_user_member = covering;
          week_data.consolidated_expected_production_prcnt =
            covering.expected_production_prcnt ?? week_data.default_expected_production_prcnt;
        } else {
          const wc =
            working_contract_template ?? this.findWorkingContractTemplateForUser(row.user_id) ?? this.findAnyWorkingContractOnRow(row_idx);
          if (wc == null) {
            continue;
          }
          row.weeks[week_key_it] = this.companyProductUserWeekData({
            company_product_user_member: covering,
            user: covering.user ?? ({ id: row.user_id, name: row.user_name, surnames: row.user_surnames } as User),
            working_contract: wc,
          });
        }
      } else if (week_data != null && touched_member_ids.has(week_data.company_product_user_member?.id)) {
        delete row.weeks[week_key_it];
      }
    }
  }

  private findWorkingContractTemplateForUser(user_id: number): WorkingContract | null {
    for (const row of this.dataSource.data) {
      if (row.user_id !== user_id) {
        continue;
      }
      const week = Object.values(row.weeks).find(w => w?.working_contract != null);
      if (week?.working_contract != null) {
        return week.working_contract;
      }
    }
    return null;
  }

  private findAnyWorkingContractOnRow(row_idx: number): WorkingContract | null {
    const week = Object.values(this.dataSource.data[row_idx].weeks).find(w => w?.working_contract != null);
    return week?.working_contract ?? null;
  }

  private rebuildCompanyProductHoursRoleWeekly(): void {
    this.company_products_hours_role_weekly = {};
    this.weeks.forEach(week => {
      const week_key =
        dayjs.tz(week.from, 'Europe/Madrid').format('YYYY_MM_DD') + '_' + dayjs.tz(week.to, 'Europe/Madrid').format('YYYY_MM_DD');
      this.processWeekUserJourneyToAddToCompanyProductHoursByRoleWeekly(week_key);
    });
  }

  private withResolvedMemberRole(member: CompanyProductUserMember): CompanyProductUserMember {
    const role =
      member.company_product_user_member_role ??
      this.companyProductUserMemberRoles()?.find(r => r.id === member.company_product_user_member_role_id);
    return {
      ...member,
      company_product_user_member_role: role!,
    };
  }

  private markContractYearSummaryUnknownForProduct(company_product_id: number): void {
    const impacted_contract_ids = new Set(
      this.dataSource.data.filter(r => r.company_product_id === company_product_id).map(r => r.contract_id)
    );
    impacted_contract_ids.forEach(cid => {
      if (this.contract_year_summary[cid]) {
        this.contract_year_summary[cid].previstas = null;
        this.contract_year_summary[cid].previstas_netas = null;
        this.contract_year_summary[cid].planificadas = null;
      }
    });
  }

  private addAndMergeMonthsData(contract_data: ProductionPlanificationContract) {
    const company_product_key: string = `company_product_${contract_data.contract.company_product_id}`;
    Object.keys(contract_data.months).forEach(month_key => {
      if (!this.company_products_monthly[company_product_key]) {
        this.company_products_monthly[company_product_key] = {
          contract_id: contract_data.contract.id,
          company_product_id: contract_data.contract.company_product_id,
          company_product_title: contract_data.contract.company_product.title,
          months: {},
        };
      }

      this.company_products_monthly[company_product_key].months[month_key] = {
        expected_hours_budgetable: contract_data.months[month_key].expected_hours_budgetable,
        expected_billing: contract_data.months[month_key].expected_billing,
        done_hours_for_client: contract_data.months[month_key].done_hours_for_client ?? 0,
      };
    });
  }

  private addToData(data: ProductionPlanification) {
    const data_table: { [key: string]: ProductionPlanificationRow } = {};

    // 1. Init with current datasource data
    this.dataSource.data.forEach(row => {
      const key = `contract_${row.contract_id}_user_${row.user_id}`;
      data_table[key] = row;
    });

    // 2. Process
    data.contracts.forEach(contract_data => {
      Object.keys(contract_data.weeks).forEach(week_key => {
        Object.keys(contract_data.weeks[week_key].users).forEach(user_key => {
          const contract_key: string = `contract_${contract_data.contract.id}`;
          const key: string = `${contract_key}_${user_key}`;
          if (!data_table[key]) {
            data_table[key] = {
              contract_id: contract_data.contract.id,
              is_first_row_of_contract: false,
              is_last_row_of_contract: false,
              company_product_id: contract_data.contract.company_product_id,
              company_product_title: contract_data.contract.company_product.title,
              user_id: contract_data.weeks[week_key].users[user_key].user.id,
              user_name: contract_data.weeks[week_key].users[user_key].user.name,
              user_surnames: contract_data.weeks[week_key].users[user_key].user.surnames,
              weeks: {},
            };
          }

          data_table[key]['weeks'][week_key] = this.companyProductUserWeekData(contract_data.weeks[week_key].users[user_key]);
        });
      });
    });

    // 3. Validem flags
    const data_res: ProductionPlanificationRow[] = this.processValidationFlags(Object.values(data_table));

    this.dataSource.data = data_res;
    this.rebuildPortionCellState();

    // 4. Calculem week company_product data
    this.weeks.forEach(week => {
      const week_key: string =
        dayjs.tz(week.from, 'Europe/Madrid').format('YYYY_MM_DD') + '_' + dayjs.tz(week.to, 'Europe/Madrid').format('YYYY_MM_DD');
      this.processWeekUserJourneyToAddToCompanyProductHoursByRoleWeekly(week_key);
    });

    this.buildWeekRoleDisplay();
    this.buildContractYearSummary();
  }

  private buildAvailableYears(): number[] {
    const current = dayjs().year();
    const years: number[] = [];
    for (let y = current - 1; y <= current + 1; y++) {
      years.push(y);
    }
    return years;
  }

  private buildContractYearSummary(): void {
    // El resumen anual (previstas, previstas_netas, planificadas) viene del backend (year_summary).
    // Solo refrescamos tooltips por si algún contrato tiene summary pero no tooltip.
    Object.keys(this.contract_year_summary).forEach(contract_id_str => {
      const contract_id = Number(contract_id_str);
      this.contract_year_summary_tooltips[contract_id] = this.buildContractYearSummaryTooltip(contract_id);
    });
  }

  private buildContractYearSummaryTooltip(contract_id: number): string {
    const s = this.contract_year_summary[contract_id];
    if (s == null) {
      return '';
    }
    const prev = (s.previstas ?? 0).toFixed(0);
    const plan = (s.planificadas ?? 0).toFixed(0);
    const year = this.selectedYear();
    let text = `Horas previstas en ${year}: ${prev}h. `;
    if (s.vacation_hours_not_taken_obligatory != null && s.vacation_hours_not_taken_obligatory > 0) {
      const vac = s.vacation_hours_not_taken_obligatory.toFixed(0);
      text += `De las previstas, ${vac}h corresponden a vacaciones no cogidas que están obligados a coger. `;
    } else {
      text +=
        'Cálculo: semanas efectivas por persona (días laborables - festivos - días personales) × horas productivas/semana x jornada. ';
    }
    text += `Horas planificadas (teóricas, pactadas con el cliente) en ${year}: ${plan}h.`;
    return text;
  }

  private buildPaymentStatusInfo(report: CompanyProductMonthlyEconomicReport): EconomicReportPaymentStatusInfo | null {
    if (report.payment_status_color === null) {
      return null;
    }
    switch (report.payment_status_color) {
      case 'green':
        return { icon: 'check_circle', tooltip: 'Todas las facturas pagadas', class: 'status-green' };
      case 'yellow':
        return { icon: 'warning', tooltip: 'Facturas vencidas sin pagar', class: 'status-yellow' };
      case 'grey':
        return { icon: 'schedule', tooltip: 'Pago pendiente sin vencer', class: 'status-grey' };
      default:
        return null;
    }
  }

  private buildWeekRoleDisplay(): void {
    this.week_role_display = {};
    const slugToShortName = this.companyProductUserMemberRoleSlugToShortName();

    Object.keys(this.company_products_hours_role_weekly).forEach(week_key => {
      this.week_role_display[week_key] = {};
      Object.keys(this.company_products_hours_role_weekly[week_key]).forEach(contract_key => {
        const contractData = this.company_products_hours_role_weekly[week_key][contract_key];
        const items: WeekRoleDisplayItem[] = [];

        (['tech-lead', 'product-designer', 'product-manager', 'developer'] as const).forEach(roleKey => {
          const roleValue = contractData[roleKey];
          const currPrcnt = this.getRoleProgressCurrPrcnt(
            week_key,
            parseInt(contract_key.replace('contract_', ''), 10),
            roleKey,
            roleValue
          );
          const optimalPrcnt = this.getRoleProgressOptimalPrcnt(week_key, parseInt(contract_key.replace('contract_', ''), 10), roleKey);
          const percentage = optimalPrcnt === 0 ? 0 : (currPrcnt / optimalPrcnt) * 100;
          const diff = optimalPrcnt - currPrcnt;
          let color: string;
          if (diff > 5 || diff < -5) color = 'red';
          else if (diff > 2 || diff < -2) color = 'yellow';
          else color = 'accent';

          if (roleValue > 0 || optimalPrcnt > 0) {
            items.push({
              roleKey,
              roleValue,
              currPrcnt,
              optimalPrcnt,
              percentage,
              color,
              shortName: slugToShortName[roleKey] ?? roleKey,
            });
          }
        });

        this.week_role_display[week_key][contract_key] = items;
      });
    });
  }

  private cleanupPusherSubscription(): void {
    if (this.currentPusherChannel) {
      this.pusher.unsubscribeFromChannel(this.currentPusherChannel);
      this.currentPusherChannel = null;
    }
  }

  private distributeTechLeadInCompanyProductHoursByRoleWeekly(week_key: string) {
    Object.keys(this.company_products_hours_role_weekly[week_key]).forEach(contract_key => {
      const currTechLeadHours = this.company_products_hours_role_weekly[week_key][contract_key]['tech-lead'];

      // Solo redistribuir si hay tech-lead asignado
      if (currTechLeadHours > 0) {
        const contractData = this.company_products_hours_role_weekly[week_key][contract_key];
        const hasProductDesigner = contractData['product-designer'] > 0;
        const optimalConfig = hasProductDesigner
          ? this.optimal_team_balance_configuration.with_product_design_prcnt
          : this.optimal_team_balance_configuration.without_product_design_prcnt;

        const optimalTechLeadHours = (optimalConfig.tech_lead / 100) * contractData['total'];
        const diffTechLeadHours = currTechLeadHours - optimalTechLeadHours;

        this.company_products_hours_role_weekly[week_key][contract_key]['tech-lead'] -= diffTechLeadHours;
        this.company_products_hours_role_weekly[week_key][contract_key]['developer'] += diffTechLeadHours;
      }
    });
  }

  private expectedProductionPrcntKey(row_idx: number, date_idx: number): string {
    return `${row_idx}_${date_idx}`;
  }

  private fetchEconomicReports(year: number) {
    this.api
      .getCompanyProductMonthlyEconomicReports({
        year,
        with: 'company_product_monthly_economic_report_invoice_conciliations.invoice',
        append: COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_PRODUCTION_PLANIFICATION_APPEND,
      })
      .subscribe({
        next: response => {
          const by_key: Record<string, EconomicReportEntry> = {};
          for (const report of response.data) {
            const month_key = dayjs(report.billed_start_date).format('YYYY_MM');
            by_key[`company_product_${report.company_product_id}_${month_key}`] = {
              report,
              payment_status_info: this.buildPaymentStatusInfo(report),
            };
          }
          this.economic_reports_by_key.set(by_key);
        },
        error: err => {
          console.error('Error fetching company_product monthly economic reports:', err);
        },
      });
  }

  private fetchProductionPlanification(year: number) {
    this.loading.set(true);
    this.progressPercent.set(null);
    this.longTaskError.set(null);
    this.dataSource.data = [];
    this.customPortionModeOverrides.set({});
    this.portionDisplayModes.set([]);
    this.portionInputValues.set([]);
    this.portionSelectValues.set([]);
    this.customPortionValues = {};
    this.expectedProductionPrcntInputValues.set([]);
    this.expectedProductionPrcntDrafts = {};
    this.cleanupPusherSubscription();

    this.api.getProductionPlanification(year).subscribe({
      next: response => {
        const withData = response as { data?: ProductionPlanification; calculated_at?: string };
        const asLongTask = response as LongTask;

        if (withData.data != null && withData.calculated_at != null) {
          this.calculated_at.set(withData.calculated_at);
          this.process(withData.data);
          this.loading.set(false);
          return;
        }

        if (asLongTask.pusher_channel_to_check != null && asLongTask.id != null) {
          if (asLongTask.error_message) {
            console.error('Error in production planification:', asLongTask.error_message);
            this.longTaskError.set(asLongTask.error_message);
            this.loading.set(false);
            return;
          }
          if (asLongTask.data && typeof asLongTask.data === 'object' && 'progress' in asLongTask.data) {
            const progressValue = (asLongTask.data as { progress?: number }).progress;
            this.progressPercent.set(progressValue ?? null);
          }
          this.currentPusherChannel = asLongTask.pusher_channel_to_check;
          this.pusher.subscribeToLongTaskChannel(this.currentPusherChannel, (data: LongTaskPusherSerialized) =>
            this.handleLongTaskUpdate(data)
          );
          this.pusher.subscribeToLongTaskProgress(this.currentPusherChannel, (data: { progress?: number; message?: string }) => {
            this.progressPercent.set(data.progress ?? null);
          });
          return;
        }

        this.loading.set(false);
      },
      error: err => {
        console.error('Error fetching production planification:', err);
        this.loading.set(false);
      },
    });
  }

  private getCurrentPortionValue(row_idx: number, date_idx: number): number {
    const row = this.dataSource.data[row_idx];
    const weekKey = this.dates[date_idx]?.week_key;
    const value = row?.weeks?.[weekKey]?.company_product_user_member?.portion_of_productive_work_day;
    return Number(value ?? 0);
  }

  private handleLongTaskUpdate(data: LongTaskPusherSerialized) {
    if (data.status === 'completed') {
      // Cuando la long task termina, volvemos a pedir la planificación (que ya estará en caché).
      this.fetchProductionPlanification(this.selectedYear());
      this.cleanupPusherSubscription();
    } else if (data.status === 'failed') {
      console.error('Production planification task failed');
      this.loading.set(false);
      this.progressPercent.set(null);
      this.longTaskError.set('Se ha producido un error al recalcular la planificación.');
      this.cleanupPusherSubscription();
    }
    // For other statuses (like 'processing'), we just wait
  }

  private initColumnKeys() {
    // Iterando por días desde this.start_date hasta this.end_date con dayjs
    const dynamic_columns: string[] = [];
    const dynamic_columns_top: string[] = [];
    let current = dayjs(this.start_date);
    const last = dayjs(this.end_date);
    const dates: ProductionPlanificationDate[] = [];
    const now = dayjs(this.now);
    const current_week_start = now.startOf('week');
    while (current.isSameOrBefore(last, 'day')) {
      let days_in_month: number = 0;
      if (dayjs(this.start_date).month() === current.month()) {
        days_in_month = Math.abs(dayjs(this.start_date).diff(current.endOf('month'), 'days')) + 1;
      } else if (dayjs(this.end_date).month() === current.month()) {
        days_in_month = Math.abs(dayjs(this.end_date).startOf('month').diff(dayjs(this.end_date), 'days')) + 1;
      } else {
        days_in_month = current.daysInMonth();
      }

      const dateObj = current.toDate();
      const isPastMonth = current.year() < now.year() || (current.year() === now.year() && current.month() < now.month());

      const week_start = dayjs(dateObj).startOf('week');
      dates.push({
        date: dateObj,
        is_first_day_of_week: current.isoWeekday() === 1,
        is_first_day_of_month: current.isSame(this.start_date, 'day') || current.date() === 1,
        month_colspan: days_in_month,
        week_key: week_start.format('YYYY_MM_DD') + '_' + dayjs(dateObj).endOf('week').format('YYYY_MM_DD'),
        month_key: dayjs(dateObj).format('YYYY_MM'),
        week_colspan: 1,
        is_past_month: isPastMonth,
        is_week_editable: week_start.isSameOrAfter(current_week_start, 'day'),
      });
      dynamic_columns.push(current.format('YYYY_MM_DD'));
      dynamic_columns_top.push(current.format('YYYY_MM_DD') + '-top');
      current = current.add(1, 'day');
    }

    // Ajustar is_first_day_of_week y colspan semanal teniendo en cuenta solo los días dentro del rango actual.
    for (let i = 0; i < dates.length; i++) {
      if (i === 0 || dates[i].week_key !== dates[i - 1].week_key) {
        let span = 1;
        while (i + span < dates.length && dates[i + span].week_key === dates[i].week_key) {
          span++;
        }
        dates[i].week_colspan = span;
        dates[i].is_first_day_of_week = true;
      } else {
        dates[i].week_colspan = 0;
        dates[i].is_first_day_of_week = false;
      }
    }

    this.dates = dates;
    this.dynamic_columns = dynamic_columns;
    this.dynamic_columns_top = dynamic_columns_top;
    this.columns = [...this.fixed_columns, ...this.dynamic_columns];
    this.columns_top = [...this.fixed_columns_top, ...this.dynamic_columns_top];
  }

  private isPresetPortionValue(value: number): boolean {
    const presetPortionValues = [0, 0.25, 0.5, 0.75, 1];
    return presetPortionValues.some(v => Math.abs(v - value) < 0.000001);
  }

  private normalizeRoleKeyForOptimal(roleKey: string): 'tech_lead' | 'product_designer' | 'product_manager' | 'developer' | null {
    switch (roleKey) {
      case 'tech-lead':
        return 'tech_lead';
      case 'product-designer':
        return 'product_designer';
      case 'product-manager':
        return 'product_manager';
      case 'developer':
        return 'developer';
      default:
        return null;
    }
  }

  private patchPortionCellMatrices(
    row_idx: number,
    date_idx: number,
    patch: { mode?: 'input' | 'select'; input?: number; select?: number | null }
  ): void {
    if (patch.mode != null) {
      this.portionDisplayModes.update(curr => {
        const next = curr.map(row => (row != null ? [...row] : []));
        if (next[row_idx] == null) {
          next[row_idx] = [];
        }
        next[row_idx][date_idx] = patch.mode!;
        return next;
      });
    }
    if (patch.input !== undefined) {
      this.portionInputValues.update(curr => {
        const next = curr.map(row => (row != null ? [...row] : []));
        if (next[row_idx] == null) {
          next[row_idx] = [];
        }
        next[row_idx][date_idx] = patch.input!;
        return next;
      });
    }
    if (patch.select !== undefined) {
      this.portionSelectValues.update(curr => {
        const next = curr.map(row => (row != null ? [...row] : []));
        if (next[row_idx] == null) {
          next[row_idx] = [];
        }
        next[row_idx][date_idx] = patch.select!;
        return next;
      });
    }
  }

  private process(data: ProductionPlanification & { calculated_at?: string }) {
    this.contract_year_summary = {};
    this.contract_year_summary_tooltips = {};
    const year = this.selectedYear();
    this.start_date = dayjs().year(year).startOf('year').toDate();
    this.end_date = dayjs().year(year).endOf('year').toDate();
    if (data.calculated_at != null) {
      this.calculated_at.set(data.calculated_at);
    }
    this.weeks = data.weeks;
    this.initColumnKeys();
    this.optimal_team_balance_configuration = data.optimal_team_balance_configuration;
    data.contracts.forEach(contract_data => {
      this.addAndMergeMonthsData(contract_data);
      if (contract_data.year_summary) {
        const s = contract_data.year_summary;
        const cid = contract_data.contract.id;
        const vacation = s.vacation_hours_not_taken_obligatory ?? 0;
        this.contract_year_summary[cid] = {
          previstas: s.previstas,
          previstas_netas: Math.max(0, s.previstas - vacation),
          planificadas: s.planificadas,
          vacation_hours_not_taken_obligatory: vacation,
        };
        this.contract_year_summary_tooltips[cid] = this.buildContractYearSummaryTooltip(cid);
      }
    });
    this.addToData(data);
  }

  private processValidationFlags(data: ProductionPlanificationRow[]): ProductionPlanificationRow[] {
    // data structures necessary
    let last_contract_id: number | null = null;
    this.jornadas_laborales = {}; // week_key --> user_key

    data.forEach((row, i) => {
      // 1. Processem flags last i first i assignem
      row.is_first_row_of_contract = last_contract_id === null || last_contract_id !== row.contract_id;
      row.is_last_row_of_contract = !data[i + 1] || data[i + 1].contract_id !== row.contract_id;
      last_contract_id = row.contract_id;

      // Càlcul de flags
      let last_company_product_user_member_role_id: number | null = null;
      let last_journey: number | null = null;
      let last_department_category_id: number | null = null;
      let last_expected_production_prcnt: number | null = null;

      Object.keys(row.weeks).forEach(week_key => {
        const user_key: string = `user_${row.user_id}`;

        // 2. Recollect user data per jornada
        if (!this.jornadas_laborales[week_key]) this.jornadas_laborales[week_key] = {};
        if (!this.jornadas_laborales[week_key][user_key]) this.jornadas_laborales[week_key][user_key] = 0;

        this.jornadas_laborales[week_key][user_key] +=
          row.weeks[week_key].company_product_user_member.portion_of_productive_work_day *
          (row.weeks[week_key].working_contract.active_working_contract_variable_condition.hours_per_week_expected -
            row.weeks[week_key].working_contract.active_working_contract_variable_condition.active_department_category
              .active_department_category_variable_condition!.max_non_productive_hours_per_week_assuming_fulltime);

        // 3. Calculem flags de canvi de condicions
        row.weeks[week_key].has_company_product_user_member_role_change =
          last_company_product_user_member_role_id !== null &&
          row.weeks[week_key].company_product_user_member.company_product_user_member_role_id !== last_company_product_user_member_role_id;
        row.weeks[week_key].has_journey_change =
          last_journey !== null && row.weeks[week_key].company_product_user_member.portion_of_productive_work_day !== last_journey;
        row.weeks[week_key].has_department_category_change =
          last_department_category_id !== null &&
          row.weeks[week_key].working_contract.active_working_contract_variable_condition.active_department_category.id !==
            last_department_category_id;
        row.weeks[week_key].has_expected_production_prcnt_change =
          last_expected_production_prcnt !== null &&
          row.weeks[week_key].consolidated_expected_production_prcnt !== last_expected_production_prcnt;

        // 4. Assignem
        last_company_product_user_member_role_id = row.weeks[week_key].company_product_user_member.company_product_user_member_role_id;
        last_journey = row.weeks[week_key].company_product_user_member.portion_of_productive_work_day;
        last_department_category_id =
          row.weeks[week_key].working_contract.active_working_contract_variable_condition.active_department_category.id;
        last_expected_production_prcnt = row.weeks[week_key].consolidated_expected_production_prcnt;
      });
    });

    const journeyToleranceHours = 0.05;
    data.forEach(row => {
      Object.keys(row.weeks).forEach(week_key => {
        const user_key: string = `user_${row.user_id}`;
        const total_assigned = this.jornadas_laborales[week_key]?.[user_key] ?? 0;
        const wc = row.weeks[week_key].working_contract.active_working_contract_variable_condition;
        const max_non_productive =
          wc.active_department_category.active_department_category_variable_condition
            ?.max_non_productive_hours_per_week_assuming_fulltime ?? 0;
        const max_productive_week = wc.hours_per_week_expected - max_non_productive;
        row.weeks[week_key].has_journey_inconsistency = total_assigned + journeyToleranceHours < max_productive_week;
      });
    });

    return data;
  }

  private processWeekUserJourneyToAddToCompanyProductHoursByRoleWeekly(week_key: string) {
    if (!this.company_products_hours_role_weekly[week_key]) {
      this.company_products_hours_role_weekly[week_key] = {};
    }

    this.dataSource.data.forEach(row => {
      if (row.weeks[week_key]) {
        const contract_key: string = `contract_${row.contract_id}`;

        if (!this.company_products_hours_role_weekly[week_key][contract_key]) {
          this.company_products_hours_role_weekly[week_key][contract_key] = {
            total: 0,
            'tech-lead': 0,
            'product-designer': 0,
            'product-manager': 0,
            developer: 0,
          };
        }

        const amount_worked_week: number =
          row['weeks'][week_key].company_product_user_member.portion_of_productive_work_day *
          (row['weeks'][week_key].working_contract.active_working_contract_variable_condition.hours_per_week_expected -
            (row['weeks'][week_key].working_contract.active_working_contract_variable_condition.active_department_category
              .active_department_category_variable_condition?.max_non_productive_hours_per_week_assuming_fulltime ?? 0));

        this.company_products_hours_role_weekly[week_key][contract_key].total += amount_worked_week;
        const cat_slug: 'tech-lead' | 'product-designer' | 'product-manager' | 'developer' = row['weeks'][week_key]
          .company_product_user_member.company_product_user_member_role.slug as any;
        this.company_products_hours_role_weekly[week_key][contract_key][cat_slug] += amount_worked_week;
      }
    });

    this.distributeTechLeadInCompanyProductHoursByRoleWeekly(week_key);
  }

  private companyProductUserWeekData(user_data: {
    company_product_user_member: CompanyProductUserMember;
    user: User;
    working_contract: WorkingContract;
  }): CompanyProductUserWeekData {
    const active_working_contract_variable_condition: WorkingContractVariableConditionCustom = {
      ...user_data.working_contract.working_contract_variable_conditions[0],
      ...{ active_department_category: user_data.working_contract.working_contract_variable_conditions[0].department_categories[0] },
    };

    active_working_contract_variable_condition.department_categories.forEach(dc => {
      dc.active_department_category_variable_condition = dc.department_category_variable_conditions[0];
    });

    let default_expected_production_prcnt = 0;
    if (active_working_contract_variable_condition.expected_production_prcnt !== null) {
      default_expected_production_prcnt = active_working_contract_variable_condition.expected_production_prcnt;
    } else {
      active_working_contract_variable_condition.department_categories.forEach(dc => {
        dc.department_category_variable_conditions.forEach(dcsc => {
          if (dcsc.expected_production_prcnt > default_expected_production_prcnt) {
            default_expected_production_prcnt = dcsc.expected_production_prcnt;
          }
        });
      });
    }

    const consolidated_expected_production_prcnt =
      user_data.company_product_user_member.expected_production_prcnt ?? default_expected_production_prcnt;

    const working_contract_custom: WorkingContractCustom = {
      ...user_data.working_contract,
      ...{ active_working_contract_variable_condition: active_working_contract_variable_condition },
    };
    return {
      company_product_user_member: user_data.company_product_user_member,
      default_expected_production_prcnt: default_expected_production_prcnt,
      consolidated_expected_production_prcnt: consolidated_expected_production_prcnt,
      working_contract: working_contract_custom,
      has_journey_inconsistency: false,
      has_company_product_user_member_role_change: false,
      has_journey_change: false,
      has_department_category_change: false,
      has_expected_production_prcnt_change: false,
    };
  }

  private rebuildExpectedProductionPrcntCellState(): void {
    const values: (number | null)[][] = [];

    this.dataSource.data.forEach((row, row_idx) => {
      values[row_idx] = [];
      this.dates.forEach((date, date_idx) => {
        const pum = row.weeks?.[date.week_key]?.company_product_user_member;
        if (pum == null) {
          return;
        }
        const key = this.expectedProductionPrcntKey(row_idx, date_idx);
        const draft = this.expectedProductionPrcntDrafts[key];
        values[row_idx][date_idx] = draft === undefined ? pum.expected_production_prcnt : draft === '' ? null : draft;
      });
    });

    this.expectedProductionPrcntInputValues.set(values);
  }

  private rebuildPortionCellState(): void {
    const modes: ('input' | 'select')[][] = [];
    const inputs: number[][] = [];
    const selects: (number | null)[][] = [];

    this.dataSource.data.forEach((row, row_idx) => {
      modes[row_idx] = [];
      inputs[row_idx] = [];
      selects[row_idx] = [];
      this.dates.forEach((date, date_idx) => {
        if (row.weeks?.[date.week_key]?.company_product_user_member == null) {
          return;
        }
        const key = this.customPortionKey(row_idx, date_idx);
        const current = this.getCurrentPortionValue(row_idx, date_idx);
        const draft = this.customPortionValues[key];

        modes[row_idx][date_idx] = this.resolvePortionDisplayMode(row_idx, date_idx);
        inputs[row_idx][date_idx] = draft != null ? draft : current;
        selects[row_idx][date_idx] = this.isPresetPortionValue(current) ? current : null;
      });
    });

    this.portionDisplayModes.set(modes);
    this.portionInputValues.set(inputs);
    this.portionSelectValues.set(selects);
    this.rebuildExpectedProductionPrcntCellState();
  }

  private resolvePortionDisplayMode(row_idx: number, date_idx: number): 'input' | 'select' {
    const key = this.customPortionKey(row_idx, date_idx);
    const override = this.customPortionModeOverrides()[key];
    if (override != null) {
      return override;
    }
    return this.isPresetPortionValue(this.getCurrentPortionValue(row_idx, date_idx)) ? 'select' : 'input';
  }

  private wouldMemberMatchPreviousWeekAfterChange(
    current_pum: CompanyProductUserMember,
    previous_pum: CompanyProductUserMember,
    field: 'company_product_user_member_role_id' | 'portion_of_productive_work_day' | 'expected_production_prcnt',
    value: number | null
  ): boolean {
    if (current_pum.id === previous_pum.id) {
      return false;
    }

    const next_role_id = field === 'company_product_user_member_role_id' ? value : current_pum.company_product_user_member_role_id;
    const next_portion = field === 'portion_of_productive_work_day' ? value : current_pum.portion_of_productive_work_day;
    const next_expected_production_prcnt = field === 'expected_production_prcnt' ? value : current_pum.expected_production_prcnt;

    const same_portion =
      next_portion != null &&
      previous_pum.portion_of_productive_work_day != null &&
      Math.abs(Number(next_portion) - Number(previous_pum.portion_of_productive_work_day)) < 0.000001;

    return (
      Number(next_role_id) === Number(previous_pum.company_product_user_member_role_id) &&
      same_portion &&
      (next_expected_production_prcnt ?? null) === (previous_pum.expected_production_prcnt ?? null)
    );
  }
}

interface ProductionPlanificationDate {
  date: Date;
  is_first_day_of_week: boolean;
  is_first_day_of_month: boolean;
  month_colspan: number;
  week_key: string;
  month_key: string;
  week_colspan: number;
  is_past_month?: boolean;
  is_week_editable: boolean;
}

interface EconomicReportPaymentStatusInfo {
  icon: string;
  tooltip: string;
  class: string;
}

interface EconomicReportEntry {
  report: CompanyProductMonthlyEconomicReport;
  payment_status_info: EconomicReportPaymentStatusInfo | null;
}

interface WeekRoleDisplayItem {
  roleKey: string;
  roleValue: number;
  currPrcnt: number;
  optimalPrcnt: number;
  percentage: number;
  color: string;
  shortName: string;
}

interface ProductionPlanificationRow {
  contract_id: number;
  is_first_row_of_contract: boolean;
  is_last_row_of_contract: boolean;
  company_product_id: number;
  company_product_title: string;

  user_id: number;
  user_name: string;
  user_surnames: string;

  weeks: { [key: string]: CompanyProductUserWeekData };
}

interface ProductionPlanificationMonthlyForCompanyProduct {
  contract_id: number;
  company_product_id: number;
  company_product_title: string;
  months: {
    [key: string]: {
      // months
      expected_hours_budgetable: number;
      expected_billing: number;
      done_hours_for_client?: number;
    };
  };
}

interface CompanyProductUserWeekData {
  company_product_user_member: CompanyProductUserMember;
  default_expected_production_prcnt: number;
  consolidated_expected_production_prcnt: number;
  working_contract: WorkingContractCustom;

  has_journey_inconsistency: boolean;

  has_company_product_user_member_role_change: boolean;
  has_journey_change: boolean;
  has_department_category_change: boolean;
  has_expected_production_prcnt_change: boolean;
}

interface WorkingContractCustom extends WorkingContract {
  active_working_contract_variable_condition: WorkingContractVariableConditionCustom;
}

interface WorkingContractVariableConditionCustom extends WorkingContractVariableCondition {
  active_department_category: DepartmentCategory;
  department_categories: DepartmentCategory[];
}

const COMPANY_PRODUCT_MONTHLY_ECONOMIC_REPORT_PRODUCTION_PLANIFICATION_APPEND = 'ro_total_hours_billed,payment_status_color';
